"use client";

import type { useChat } from "@ai-sdk/react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { AlertTriangleIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { useRouter } from "@/lib/navigation";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { DELETE_DASHBOARD_ITEM_NAME } from "./constants";
import type { DeleteDashboardItemInput } from "./schema";

type DashboardItemType = ReturnType<typeof graphql.scalar<"DashboardItemType">>;

const DashboardItemQuery = graphql(`
  query DeleteDashboardItemConfirmQuery($dashboardId: ID!, $dashboardItemId: ID!) {
    dashboard: node(id: $dashboardId) {
      __typename
      ... on Dashboard {
        id
        name
      }
    }
    item: node(id: $dashboardItemId) {
      __typename
      ... on DashboardItemDivider {
        id
        name
        type
      }
      ... on DashboardItemMetricWithTimeSeries {
        id
        name
        type
        featureID
      }
      ... on DashboardItemPageTransitionRate {
        id
        name
        type
        transitionFeatureID: featureID
      }
    }
  }
`);

const FeatureQuery = graphql(`
  query DeleteDashboardItemFeatureQuery($featureId: ID!) {
    node(id: $featureId) {
      __typename
      ... on IndexWorker {
        id
        name
      }
      ... on SearchAnalyticsView {
        id
        name
      }
      ... on UserAnalyticsView {
        id
        name
      }
      ... on SearchAnalytics {
        id
      }
      ... on UserAnalytics {
        id
      }
    }
  }
`);

function getItemTypeLabel(
  type: DashboardItemType | null,
  i18n: ReturnType<typeof useLingui>["i18n"],
) {
  switch (type) {
    case "DIVIDER":
      return i18n._(msg`Divider`);
    case "IW_CRAWL":
      return i18n._(msg`Crawled Rate`);
    case "IW_INDEX":
      return i18n._(msg`Indexed Rate`);
    case "IW_NOT_INDEX":
      return i18n._(msg`Not Indexed Rate`);
    case "SA_CLICK":
      return i18n._(msg`Clicks`);
    case "SA_IMP":
      return i18n._(msg`Impressions`);
    case "SA_CTR":
      return i18n._(msg`CTR`);
    case "SA_RANK":
      return i18n._(msg`Avg. position`);
    case "SA_UNIQ_QUERY":
      return i18n._(msg`Unique queries`);
    case "SA_UNIQ_URL":
      return i18n._(msg`Unique URLs`);
    case "UA_SESSION_COUNT":
      return i18n._(msg`Sessions`);
    case "UA_UNIQUE_USER_COUNT":
      return i18n._(msg`Unique users`);
    case "UA_CONVERSION_RATE":
      return i18n._(msg`Conversion rate`);
    case "UA_CONVERSION_COUNT":
      return i18n._(msg`Sessions with conversions`);
    case "UA_SURF_RATE":
      return i18n._(msg`Surf rate`);
    case "UA_ENGAGEMENT_RATE":
      return i18n._(msg`Engagement rate`);
    case "UA_PAGE_VIEW_EVENT_COUNT":
      return i18n._(msg`Page views`);
    case "UA_PAGE_TRANSITION_RATE":
      return i18n._(msg`Page transition rate`);
    default:
      return "-";
  }
}

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

/**
 * ダッシュボードアイテム削除の簡易確認 UI。
 * 対象 ID を表示し、破壊的操作であることを明示する。
 */
export function DeleteDashboardItemConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const { i18n } = useLingui();
  const router = useRouter();
  const toolName = isToolUIPart(part) ? getToolName(part) : null;
  const isTarget = toolName === DELETE_DASHBOARD_ITEM_NAME;
  const isInputAvailable = part.state === "input-available";
  const input = (part.input ?? {}) as Partial<DeleteDashboardItemInput>;
  const dashboardId = input.dashboardId ?? "";
  const dashboardItemId = input.dashboardItemId ?? "";

  const [{ data, fetching, error }] = useQuery({
    query: DashboardItemQuery,
    variables: { dashboardId, dashboardItemId },
    pause: !isTarget || !isInputAvailable || !dashboardId || !dashboardItemId,
  });

  const dashboard =
    data?.dashboard?.__typename === "Dashboard" ? data.dashboard : null;
  const item =
    data?.item?.__typename === "DashboardItemDivider" ||
    data?.item?.__typename === "DashboardItemMetricWithTimeSeries" ||
    data?.item?.__typename === "DashboardItemPageTransitionRate"
      ? data.item
      : null;
  const itemType: DashboardItemType | null = item?.type ?? null;
  const featureId =
    item?.__typename === "DashboardItemPageTransitionRate"
      ? item.transitionFeatureID
      : item?.__typename === "DashboardItemMetricWithTimeSeries"
        ? item.featureID
        : null;

  const [
    { data: featureData, fetching: featureFetching, error: featureError },
  ] = useQuery({
    query: FeatureQuery,
    variables: { featureId: featureId ?? "" },
    pause: !featureId,
  });

  const featureNode = featureData?.node;
  const featureName =
    featureId &&
    featureNode &&
    "name" in featureNode &&
    typeof (featureNode as { name?: unknown }).name === "string"
      ? (featureNode as { name: string }).name
      : featureId
        ? i18n._(
            msg({
              id: "default-view.name",
              message: "All",
              comment: "Default view label (overall)",
            }),
          )
        : "-";

  const pendingInfo = fetching && !item;
  const pendingFeature = featureFetching && !!featureId;
  const dashboardLoadFailed = !!error || (!pendingInfo && !dashboard);
  const itemLoadFailed = !!error || (!pendingInfo && !item);
  const featureLoadFailed =
    !!featureId && (!!featureError || (!pendingFeature && !featureNode));
  const blockDelete =
    dashboardLoadFailed || itemLoadFailed || featureLoadFailed;

  if (!isTarget || !isInputAvailable) return null;

  const handleSuccess = () => {
    router.refresh();
  };

  return (
    <div className="p-4 text-sm space-y-4">
      <div className="space-y-2">
        <div className="font-medium">
          <Trans>Do you want to delete this dashboard item?</Trans>
        </div>
        <div className="space-y-2 rounded border bg-muted/30 p-3 text-sm">
          <div>
            <div className="text-xs text-muted-foreground">
              <Trans>Dashboard</Trans>
            </div>
            {fetching && !dashboard ? (
              <Skeleton className="mt-1 h-4 w-36 bg-gray-400 dark:bg-gray-600" />
            ) : (
              <div className="mt-1 font-semibold">{dashboard?.name ?? "-"}</div>
            )}
          </div>
          {item?.name ? (
            <div>
              <div className="text-xs text-muted-foreground">
                <Trans>Name</Trans>
              </div>
              <div className="mt-1 font-semibold">{item.name}</div>
            </div>
          ) : null}
          <div>
            <div className="text-xs text-muted-foreground">
              <Trans>Item Type</Trans>
            </div>
            {pendingInfo ? (
              <Skeleton className="mt-1 h-4 w-32 bg-gray-400 dark:bg-gray-600" />
            ) : (
              <div className="mt-1 font-semibold">
                {getItemTypeLabel(itemType, i18n)}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs text-muted-foreground">
              <Trans>Resource (job, view, etc.)</Trans>
            </div>
            {pendingFeature ? (
              <Skeleton className="mt-1 h-4 w-40 bg-gray-400 dark:bg-gray-600" />
            ) : (
              <div className="mt-1 font-semibold">{featureName}</div>
            )}
          </div>
        </div>
        {dashboardLoadFailed || itemLoadFailed || featureLoadFailed ? (
          <Alert variant="destructive" className="text-xs">
            <AlertTitle>
              <Trans>Unable to load the target data</Trans>
            </AlertTitle>
            <AlertDescription>
              <Trans>
                It may already be deleted. Please reload and confirm before
                retrying.
              </Trans>
            </AlertDescription>
          </Alert>
        ) : null}
      </div>
      {!blockDelete ? (
        <Alert variant="destructive" className="text-xs">
          <AlertTriangleIcon className="h-4 w-4" />
          <AlertTitle>
            <Trans>This action is irreversible</Trans>
          </AlertTitle>
          <AlertDescription>
            <Trans>
              Deleting an item removes the card from the dashboard for everyone
              in the workspace.
            </Trans>
          </AlertDescription>
        </Alert>
      ) : null}
      <div className="flex gap-2">
        {blockDelete ? (
          <Button size="sm" variant="secondary" disabled>
            <Trans>Delete</Trans>
          </Button>
        ) : (
          <ApproveButton
            toolCallId={part.toolCallId}
            toolName={toolName ?? DELETE_DASHBOARD_ITEM_NAME}
            addToolResult={addToolResult}
            sendMessage={sendMessage}
            variant="destructive"
            onSuccess={handleSuccess}
          >
            <Trans>Delete</Trans>
          </ApproveButton>
        )}
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={toolName ?? DELETE_DASHBOARD_ITEM_NAME}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
